import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  API,
  Field2 as Field,
} from 'vibeguide';
import Grid from '@mui/material/Grid';

function ProfileSettings({
  className = '',
  companyId = '',
  contentNetworkOwner = '',
  contentDeliverySystem = '',
  adNetworkOwner = '',
  adDeliverySystem = '',
  adProgramEnabled = false,
  adNetworkEnabled = false,
  openExchangeEnabled = false,
  isAdmin = false,
  canEdit = false,
  canViewCompany = false,
  canEnableAdProgram = false,
  canModifyLocationAdProgram = false,
  onUpdate = () => {},
}) {
  const [companyAdProgramEnabled, setCompanyAdProgramEnabled] = useState(false);

  const [companyAdNetworkEnabled, setCompanyAdNetworkEnabled] = useState(false);

  const canAdminEdit = isAdmin && canEdit;

  const onDropdownChange = (e) => {
    const {
      name,
      value,
    } = e;

    onUpdate({
      [name]: value,
    });
  };

  /**
   * When the ad program is toggled
   */
  const onToggleAdProgram = () => {
    onUpdate({
      adProgramEnabled: !adProgramEnabled,
    });
  };

  const onToggleAdNetwork = () => {
    onUpdate({
      adNetworkEnabled: !adNetworkEnabled,
    });
  };

  const onToggleOpenExchange = () => {
    onUpdate({
      openExchangeEnabled: !openExchangeEnabled,
    });
  };

  const titleCase = (string) => {
    return string?.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
  };

  /**
   * Fetch the company information to determine if the ad program is enabled at the company level
   */
  useEffect(() => {
    if (!canViewCompany || !canEnableAdProgram) {
      // do not fetch if the user cannot view the company or enable the ad program
      return;
    }

    async function fetchData() {
      const company = await API.Company.getById(companyId);

      if (company && company.adProgramEnabled) {
        setCompanyAdProgramEnabled(true);
      }

      if (company && company.adNetworkEnabled) {
        setCompanyAdNetworkEnabled(true);
      }
    }
    fetchData();
  }, []);

  return (
    <div className={classNames('ProfileSettings', className)}>
      <Grid spacing={2} container>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Field
            type="dropdown"
            label="Content Network Owner"
            name="contentNetworkOwner"
            value={titleCase(contentNetworkOwner)}
            tabIndex={10}
            dropdownProps={{
              type: 'lookup',
              attr: 'value',
              fetch: API.LookupList.contentNetworkOwners.search,
            }}
            disabled={!canAdminEdit}
            testId="location-profile-content-network-owner-dropdown"
            onChange={onDropdownChange}
            required
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Field
            type="dropdown"
            label="Content Delivery System"
            name="contentDeliverySystem"
            value={titleCase(contentDeliverySystem)}
            tabIndex={11}
            dropdownProps={{
              type: 'lookup',
              attr: 'value',
              fetch: API.LookupList.contentDeliverySystems.search,
            }}
            disabled={!canAdminEdit}
            testId="location-profile-content-delivery-system-dropdown"
            onChange={onDropdownChange}
            required
          />
        </Grid>

        {canEnableAdProgram && (
          <Grid size={{ xs: 12, lg: 6 }}>
            <Field
              type="toggle"
              label="Ad Network"
              toggleProps={{
                label: 'Enable Ad Network',
                helpText: !companyAdNetworkEnabled
                  ? 'Company ad network is disabled'
                  : null,
              }}
              value={adNetworkEnabled
                ? 'on'
                : 'off'}
              tabIndex={8}
              disabled={!canAdminEdit || !companyAdNetworkEnabled || adProgramEnabled || !canModifyLocationAdProgram}
              testId="location-profile-ad-network-toggle"
              onChange={onToggleAdNetwork}
            />
          </Grid>
        )}

        {canEnableAdProgram && (
          <Grid size={{ xs: 12, lg: 6 }}>
            <Field
              type="toggle"
              label="Ad Programs"
              toggleProps={{
                label: 'Enable Ad Programs',
                helpText: !companyAdProgramEnabled
                  ? 'Company ad program is disabled'
                  : null,
              }}
              value={adProgramEnabled
                ? 'on'
                : 'off'}
              tabIndex={9}
              disabled={!canAdminEdit || !companyAdProgramEnabled || !adNetworkEnabled || !canModifyLocationAdProgram}
              testId="location-profile-ad-program-toggle"
              onChange={onToggleAdProgram}
            />
          </Grid>
        )}

        <>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Field
              type="dropdown"
              label="Ad Delivery System"
              name="adDeliverySystem"
              value={titleCase(adDeliverySystem)}
              tabIndex={12}
              dropdownProps={{
                type: 'lookup',
                attr: 'value',
                fetch: API.LookupList.adDeliverySystems.search,
              }}
              disabled={!canAdminEdit}
              testId="location-profile-ad-delivery-system-dropdown"
              onChange={onDropdownChange}
              required={adNetworkEnabled}
            />
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Field
              type="dropdown"
              label="Ad Network Owner"
              name="adNetworkOwner"
              value={titleCase(adNetworkOwner)}
              tabIndex={13}
              dropdownProps={{
                type: 'lookup',
                attr: 'value',
                fetch: API.LookupList.adNetworkOwners.search,
              }}
              disabled={!canAdminEdit}
              testId="location-profile-ad-network-owner-dropdown"
              onChange={onDropdownChange}
              required={adNetworkEnabled}
            />
          </Grid>
        </>

        {canEnableAdProgram && (
          <Grid size={{ xs: 12, lg: 6 }}>
            <Field
              type="toggle"
              label="Open Exchange"
              toggleProps={{
                label: 'Enable Open Exchange',
                helpText: !adNetworkEnabled || !adProgramEnabled
                  ? 'Requires ad network and ad programs enabled'
                  : null,
              }}
              value={openExchangeEnabled
                ? 'on'
                : 'off'}
              tabIndex={14}
              disabled={!canAdminEdit || !adNetworkEnabled || !adProgramEnabled || !canModifyLocationAdProgram}
              testId="location-profile-open-exchange-toggle"
              onChange={onToggleOpenExchange}
            />
          </Grid>
        )}
      </Grid>
    </div>
  );
}

ProfileSettings.propTypes = {
  className: PropTypes.string,
  companyId: PropTypes.string,
  contentNetworkOwner: PropTypes.string,
  contentDeliverySystem: PropTypes.string,
  adNetworkOwner: PropTypes.string,
  adDeliverySystem: PropTypes.string,
  adProgramEnabled: PropTypes.bool,
  adNetworkEnabled: PropTypes.bool,
  openExchangeEnabled: PropTypes.bool,
  isAdmin: PropTypes.bool,
  canEdit: PropTypes.bool,
  canViewCompany: PropTypes.bool,
  canEnableAdProgram: PropTypes.bool,
  canModifyLocationAdProgram: PropTypes.bool,
  onUpdate: PropTypes.func,
};

export default ProfileSettings;
